import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  InputLabel,
  Rating,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  useTheme,
  Fade,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { motion, AnimatePresence } from "framer-motion";
import { DashboardSideMenu } from "../../../static/staticdata";
import { useSelector } from "react-redux";
import { useAccess } from "../../Auth/AccessProvider";
import ReviewsRoundedIcon from "@mui/icons-material/ReviewsRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import { useSubscription } from "../../Auth/SubscriptionContext";
import { useDialog } from "../../DialogContext";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import axios from "axios";
import { backend_api } from "../../../static/server";
import { LoadingButton } from "@mui/lab";
import {
  ArrowLeftRounded,
  ArrowRightRounded,
  AutoAwesomeRounded,
  KeyboardArrowDownRounded,
  LockRounded,
} from "@mui/icons-material";
import { useCustomTheme } from "../../../pages/LawyerPages/Settings/useTheme";
import companyLogo from "../../../assets/nav-lexmom.webp";
import smallCompanyLogo from "../../../assets/default.png";
import SidebarAICard from "./SidebarAICard";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const SideNavBar = ({ openSide, setOpenSide }) => {
  const { showDialog, closeDialog } = useDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [expandedCollapsedMenu, setExpandedCollapsedMenu] = useState(null);
  const { token } = useSelector((state) => state.user);
  const [feedSending, setFeedSending] = useState(false);
  const [issueSending, setIssueSending] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Get theme colors
  const { themeConfig } = useCustomTheme();
  const muiTheme = useTheme();

  // Define theme-aware colors
  const primaryColor =
    themeConfig.primaryColor || muiTheme.palette.primary.main;
  const secondaryColor =
    themeConfig.secondaryColor || muiTheme.palette.secondary.main;

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const { userAccess } = useSelector((state) => state.user);
  const { getAllowedModules, isHighAccess } = useAccess();
  const {
    subscription,
    plan,
    isTrial,
    loading: subscriptionLoading,
    hasActivePlan,
    ready,
  } = useSubscription();

  const allowedModules = useMemo(
    () => getAllowedModules(),
    [getAllowedModules],
  );
  const highAccess = useMemo(() => isHighAccess(), [isHighAccess]);
  const activePlan = useMemo(() => hasActivePlan(), [hasActivePlan]);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      email: "",
      rating: 0,
      feedback_type: "",
      comments: "",
    },
  });

  const onSubmit = (data) => {
    setFeedSending(true);
    try {
      const res = axios.post(`${backend_api}feedback/`, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      toast.success("Feedback submitted successfully.");
      return res;
    } catch (err) {
      toast.error("Failed to send feedback to team.");
    } finally {
      setFeedSending(false);
      closeDialog();
      reset();
    }
  };

  const IssueSubmit = (data) => {
    setFeedSending(true);
    try {
      const res = axios.post(`${backend_api}requests/`, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      toast.success("Issue reported successfully.");
      return res;
    } catch (err) {
      toast.error("Failed to send feedback to team.");
    } finally {
      setFeedSending(false);
      closeDialog();
      reset();
    }
  };

  // Filter menu based on subscription features and role access
  useEffect(() => {
    setIsLoading(true);

    if (subscriptionLoading || !ready) return;

    const hasSidebarFeature = (sidebarId) => {
      if (!sidebarId) return true;
      const featureList = plan?.features || subscription?.plan?.features;

      if (!Array.isArray(featureList) || featureList.length === 0) {
        return activePlan;
      }

      const checkFeature = (feature) => {
        if (!feature || typeof feature !== "object") return false;
        if (feature.sidebarId === sidebarId) return true;
        if (Array.isArray(feature.subMenu)) {
          return feature.subMenu.some(checkFeature);
        }
        return false;
      };

      return featureList.some(checkFeature);
    };

    const menuWithPlanAccess = DashboardSideMenu.map((item) => {
      const hasFeature = hasSidebarFeature(item.accessId);
      const locked = !hasFeature;

      let subMenu = undefined;
      if (item.subMenu && Array.isArray(item.subMenu)) {
        subMenu = item.subMenu.map((subItem) => {
          const subHasFeature = hasSidebarFeature(subItem.accessId);
          return { ...subItem, locked: !subHasFeature };
        });
      }

      return { ...item, subMenu, locked };
    });

    let finalMenu;
    if (!userAccess || !Array.isArray(userAccess) || isHighAccess()) {
      finalMenu = menuWithPlanAccess;
    } else {
      finalMenu = menuWithPlanAccess.reduce((acc, item) => {
        const isAllowedTop =
          item.accessId && allowedModules.includes(item.accessId);
        let allowedSubMenu = [];

        if (item.subMenu && Array.isArray(item.subMenu)) {
          allowedSubMenu = item.subMenu.filter((subItem) =>
            allowedModules.includes(subItem.accessId),
          );
        }

        if (isAllowedTop || allowedSubMenu.length > 0) {
          acc.push({
            ...item,
            subMenu: allowedSubMenu.length > 0 ? allowedSubMenu : undefined,
          });
        }
        return acc;
      }, []);
    }

    setFilteredMenu(finalMenu);
    setIsLoading(false);
  }, [
    userAccess,
    subscription,
    plan,
    subscriptionLoading,
    allowedModules,
    isHighAccess,
    hasActivePlan,
    ready,
  ]);

  useEffect(() => {
    const menuContainer = document.querySelector("[data-menu-container]");

    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;

      setShowScrollHint(isScrollable && !isAtBottom);
    };

    if (menuContainer) {
      handleScroll({ target: menuContainer });
      menuContainer.addEventListener("scroll", handleScroll);
      return () => menuContainer.removeEventListener("scroll", handleScroll);
    }
  }, [filteredMenu, isLoading]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setOpenSide(true);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  // Check if menu item is active
  const isMenuItemActive = (item) => {
    return (
      location.pathname === item.path ||
      (item.path === "/dashboard" &&
        location.pathname.endsWith("/dashboard")) ||
      (item.subMenu &&
        item.subMenu.some((subItem) =>
          location.pathname.endsWith(subItem.path),
        ))
    );
  };

  // Render submenu items with connecting lines
  const renderSubMenuItems = (subMenu, parentActive) => {
    return subMenu.map((subItem, subIndex) => {
      const isSubActive = location.pathname === subItem.path;
      const locked = subItem.locked;

      const subMenuContent = (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            px: openSide ? "0.25rem" : "0.75rem",
            py: "0.375rem",
            ml: openSide ? 0 : "1rem",
            borderRadius: "0.375rem",
            color: locked
              ? "text.disabled"
              : isSubActive
                ? primaryColor
                : "text.secondary",
            bgcolor: isSubActive ? hexToRgba(primaryColor, 0.1) : "transparent",
            position: "relative",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            minHeight: "36px",
            cursor: locked ? "not-allowed" : "pointer",
            opacity: locked ? 0.5 : 1,
            "&:hover": locked
              ? {}
              : {
                  bgcolor: hexToRgba(primaryColor, 0.05),
                  transform: openSide ? "scale(1.05)" : "translateX(4px)",
                  color: primaryColor,
                },
            "&::before": !openSide
              ? {
                  content: '""',
                  position: "absolute",
                  left: "-0.75rem",
                  top: "50%",
                  width: "0.5rem",
                  height: "1px",
                  bgcolor: isSubActive ? primaryColor : "divider",
                  transition: "all 0.2s ease",
                }
              : {},
            "&::after": !openSide
              ? {
                  content: '""',
                  position: "absolute",
                  left: "-0.375rem",
                  top: "50%",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  bgcolor: isSubActive ? primaryColor : "divider",
                  transform: "translateY(-50%)",
                  transition: "all 0.2s ease",
                }
              : {},
          }}
        >
          <Box
            sx={{
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              minWidth: "18px",
              justifyContent: "center",
            }}
          >
            {subItem.mIcon}
          </Box>
          {!openSide && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSubActive ? 600 : 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "0.8rem",
              }}
            >
              {subItem.mText}
            </Typography>
          )}
          {locked && (
            <LockRounded
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                fontSize: "0.9rem",
                color: "text.disabled",
              }}
            />
          )}
        </Box>
      );

      return (
        <motion.div
          key={subIndex}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{
            duration: 0.3,
            delay: subIndex * 0.05,
            ease: [0.4, 0, 0.2, 1],
          }}
        >
          <Tooltip
            title={subItem.mText}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  boxShadow: 2,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: "background.paper",
                  "&::before": {
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              },
            }}
            disableHoverListener={!openSide}
          >
            <NavLink
              to={subItem.path}
              onClick={(e) => locked && e.preventDefault()}
              style={{ textDecoration: "none", display: "block" }}
            >
              {subMenuContent}
            </NavLink>
          </Tooltip>
        </motion.div>
      );
    });
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ px: 0.5, py: 0.25 }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={openSide ? "40px" : "36px"}
            sx={{ borderRadius: "0.375rem" }}
          />
        </Box>
      ))}
    </>
  );

  // Animation variants for collapsed submenu items
  const submenuVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      x: -20,
      transition: { duration: 0.2 },
    },
    visible: (index) => ({
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.3,
        delay: index * 0.08,
        ease: [0.4, 0, 0.2, 1],
      },
    }),
    exit: {
      opacity: 0,
      scale: 0.8,
      x: -20,
      transition: { duration: 0.2 },
    },
  };

  // Render menu items
  const renderMenuItems = () => {
    if (subscriptionLoading) {
      return renderSkeleton();
    }
    return filteredMenu?.map((item, index) => {
      const hasSubMenu = item?.subMenu && item?.subMenu?.length > 0;
      const isActive = isMenuItemActive(item);
      const locked = item.locked;
      if (hasSubMenu && !locked) {
        if (openSide) {
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Box sx={{ mb: "0.5rem" }}>
                <Tooltip
                  title={item.mText}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "background.paper",
                        color: "text.primary",
                        boxShadow: 2,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      },
                    },
                    arrow: {
                      sx: {
                        color: "background.paper",
                        "&::before": {
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    onClick={() =>
                      setExpandedCollapsedMenu(
                        expandedCollapsedMenu === index ? null : index,
                      )
                    }
                    sx={{
                      p: "0.5rem",
                      borderRadius: "0.375rem",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      mb: "0.375rem",
                      color:
                        isActive || expandedCollapsedMenu === index
                          ? primaryColor
                          : "text.secondary",
                      bgcolor:
                        isActive || expandedCollapsedMenu === index
                          ? hexToRgba(primaryColor, 0.1)
                          : "transparent",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        boxShadow: `0 4px 12px ${hexToRgba(
                          primaryColor,
                          0.15,
                        )}`,
                        transform: "scale(1.05)",
                        bgcolor: hexToRgba(primaryColor, 0.05),
                        borderColor: primaryColor,
                      },
                      "&::after":
                        expandedCollapsedMenu !== index
                          ? {
                              content: '""',
                              position: "absolute",
                              bottom: "-2px",
                              right: "4px",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              bgcolor: isActive
                                ? primaryColor
                                : "text.disabled",
                              opacity: 0.6,
                            }
                          : {},
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item?.mIcon}
                    </Box>
                  </Box>
                </Tooltip>

                <AnimatePresence>
                  {expandedCollapsedMenu === index && (
                    <Box sx={{ ml: "0.75rem", position: "relative" }}>
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          position: "absolute",
                          left: "-12px",
                          top: "0",
                          width: "1px",
                          backgroundColor: "#e0e0e0",
                          zIndex: 1,
                        }}
                      />

                      {item.subMenu.map((subItem, subIndex) => {
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <motion.div
                            key={`${index}-${subIndex}`}
                            custom={subIndex}
                            variants={submenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                          >
                            <Tooltip
                              title={subItem.mText}
                              placement="right"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "background.paper",
                                    color: "text.primary",
                                    boxShadow: 2,
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: "background.paper",
                                    "&::before": {
                                      border: "1px solid",
                                      borderColor: "divider",
                                    },
                                  },
                                },
                              }}
                            >
                              <NavLink
                                to={subItem.path}
                                style={{
                                  textDecoration: "none",
                                  display: "block",
                                }}
                              >
                                <Box
                                  sx={{
                                    p: "0.375rem",
                                    borderRadius: "0.25rem",
                                    cursor: "pointer",
                                    transition:
                                      "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                    mb: "0.25rem",
                                    color: isSubActive
                                      ? primaryColor
                                      : "text.secondary",
                                    bgcolor: isSubActive
                                      ? hexToRgba(primaryColor, 0.1)
                                      : "transparent",
                                    opacity: 0.85,
                                    position: "relative",
                                    "&:hover": {
                                      boxShadow: `0 2px 8px ${hexToRgba(
                                        primaryColor,
                                        0.12,
                                      )}`,
                                      transform: "scale(1.05)",
                                      bgcolor: hexToRgba(primaryColor, 0.05),
                                      opacity: 1,
                                      borderColor: primaryColor,
                                    },
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: "-12px",
                                      top: "50%",
                                      width: "8px",
                                      height: "1px",
                                      bgcolor: isSubActive
                                        ? primaryColor
                                        : "divider",
                                      transform: "translateY(-50%)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      fontSize: "0.85rem",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {subItem?.mIcon}
                                  </Box>
                                </Box>
                              </NavLink>
                            </Tooltip>
                          </motion.div>
                        );
                      })}
                    </Box>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          );
        }

        // Render accordion for items with submenu in expanded mode
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            <Box sx={{ mb: "0.5rem" }}>
              <Accordion
                expanded={expandedAccordions?.[item.mText] || false}
                onChange={handleAccordionChange(item.mText)}
                sx={{
                  p: 0,
                  boxShadow: "none",
                  bgcolor: isActive
                    ? hexToRgba(primaryColor, 0.05)
                    : "transparent",
                  overflow: "hidden",
                  "&:before": {
                    display: "none",
                  },
                  "& .MuiAccordionSummary-root": {
                    minHeight: "auto",
                    px: "0.75rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: hexToRgba(primaryColor, 0.05),
                    },
                  },
                  "& .MuiAccordionDetails-root": {
                    px: 0,
                    py: "0rem",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        color: isActive ? primaryColor : "text.secondary",
                        fontSize: "1rem",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        color: isActive ? primaryColor : "text.secondary",
                      }}
                    >
                      {item.mIcon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? primaryColor : "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.mText}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "0.5rem",
                        top: 0,
                        bottom: 0,
                        width: "1px",
                        bgcolor: "divider",
                      },
                    }}
                  >
                    <AnimatePresence>
                      {expandedAccordions?.[item.mText] &&
                        renderSubMenuItems(item.subMenu, isActive)}
                    </AnimatePresence>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </motion.div>
        );
      }

      // Render regular menu items without submenu
      const menuItemContent = (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
          whileHover={locked ? {} : { scale: 1.02 }}
        >
          <Box
            sx={{
              p: openSide ? "0.5rem" : "0.5rem 0.75rem",
              borderRadius: "0.375rem",
              // border: "1px solid",
              // borderColor: isActive ? primaryColor : "divider",
              cursor: locked ? "not-allowed" : "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: openSide ? "column" : "row",
              justifyContent: openSide ? "center" : "flex-start",
              alignItems: "center",
              gap: openSide ? "0.125rem" : "0.5rem",
              width: "100%",
              mb: "0.5rem",
              color: locked
                ? "text.disabled"
                : isActive
                  ? primaryColor
                  : "text.secondary",
              bgcolor: isActive ? hexToRgba(primaryColor, 0.1) : "transparent",
              minHeight: openSide ? "40px" : "36px",
              opacity: locked ? 0.5 : 1,
              position: "relative",
              "&:hover": locked
                ? {}
                : {
                    boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.12)}`,
                    transform: "translateY(-1px)",
                    bgcolor: hexToRgba(primaryColor, 0.05),
                    borderColor: primaryColor,
                  },
            }}
          >
            <Box
              sx={{
                fontSize: openSide ? "1rem" : "1rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              {item?.mIcon}
            </Box>
            {!openSide && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {item?.mText}
              </Typography>
            )}
            {locked && (
              <LockRounded
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  fontSize: "1rem",
                  color: "text.disabled",
                }}
              />
            )}
          </Box>
        </motion.div>
      );

      return (
        <Tooltip
          key={index}
          title={item.mText}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: 2,
                border: "1px solid",
                borderColor: "divider",
                fontSize: "0.75rem",
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: "background.paper",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          }}
          disableHoverListener={!openSide}
        >
          <NavLink
            to={item.path}
            onClick={(e) => locked && e.preventDefault()}
            style={{ textDecoration: "none", display: "block" }}
          >
            {menuItemContent}
          </NavLink>
        </Tooltip>
      );
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: openSide ? "4.5rem" : "16rem",
        justifyContent: "flex-start",
        px: openSide ? "0.5rem" : "1rem",
        py: "0.5rem",
        backgroundColor: "#fff",
        borderTopRightRadius: "2rem",
        height: "100vh",
        position: "relative",
        transition:
          "width 300ms cubic-bezier(0.4, 0, 0.2, 1), padding 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "width, padding",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        WebkitFontSmoothing: "subpixel-antialiased",
      }}
    >
      <Box
        sx={{
          mixHeight: "2.5rem",
          width: openSide ? "2.5rem" : "9rem",
          cursor: "pointer",
          overflow: "hidden",
          transition: "width 0.3s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={openSide ? "small-logo" : "large-logo"}
            src={openSide ? smallCompanyLogo : companyLogo}
            alt="Company Logo"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = require("../../../assets/default.png");
            }}
          />
        </AnimatePresence>
      </Box>

      <Box sx={{ position: "absolute", top: "-0.5rem", right: "-0.8rem" }}>
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            setOpenSide(!openSide);
          }}
          sx={{
            zIndex: 999,
            transition: "all 0.2s ease",
          }}
        >
          {openSide ? <ArrowRightRounded /> : <ArrowLeftRounded />}
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box sx={{ position: 'relative', height: '75%' }}>
      <Box
        data-menu-container
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          p: "0.75rem 0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          height: "100%",
          position: "relative",
        }}
      >
        <Box>
          <SidebarAICard openSide={openSide} />
        </Box>
        <Tooltip
          title="AI Workspace"
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: 2,
                border: "1px solid",
                borderColor: "divider",
                fontSize: "0.75rem",
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: "background.paper",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          }}
          disableHoverListener={!openSide}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            whileHover={{ scale: 1.02 }}
          >
            <Box
              onClick={() => navigate("/dashboard/ai-workspace")}
              sx={{
                p: openSide ? "0.5rem" : "0.5rem 0.75rem",
                borderRadius: "0.375rem",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: openSide ? "column" : "row",
                justifyContent: openSide ? "center" : "flex-start",
                alignItems: "center",
                gap: openSide ? "0.125rem" : "0.5rem",
                width: "100%",
                mb: "0.5rem",
                color:
                  location.pathname === "/dashboard/ai-workspace"
                    ? primaryColor
                    : "text.secondary",
                bgcolor:
                  location.pathname === "/dashboard/ai-workspace"
                    ? hexToRgba(primaryColor, 0.1)
                    : "transparent",
                minHeight: openSide ? "40px" : "36px",
                position: "relative",
                border: `1px solid ${
                  location.pathname === "/dashboard/ai-workspace"
                    ? primaryColor
                    : "transparent"
                }`,
                "&:hover": {
                  boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.12)}`,
                  transform: "translateY(-1px)",
                  bgcolor: hexToRgba(primaryColor, 0.05),
                  borderColor: primaryColor,
                },
              }}
            >
              <Box
                sx={{
                  fontSize: openSide ? "1rem" : "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AutoAwesomeRounded />
              </Box>
              {!openSide && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight:
                      location.pathname === "/dashboard/ai-workspace"
                        ? 600
                        : 400,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: 1,
                  }}
                >
                  AI Workspace
                </Typography>
              )}
              {/* Add a small indicator for active AIs */}
              <Box
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  display: "none", // You can make this visible when there are active AIs
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)", opacity: 1 },
                    "50%": { transform: "scale(1.2)", opacity: 0.7 },
                    "100%": { transform: "scale(1)", opacity: 1 },
                  },
                }}
              />
            </Box>
          </motion.div>
        </Tooltip>
        {isLoading ? renderSkeleton() : renderMenuItems()}
      </Box>
       <AnimatePresence>
          {showScrollHint && (
            <Fade in={showScrollHint}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: "flex",
                  justifyContent: "center",
                  pb: 1,
                  background:
                    "linear-gradient(transparent, rgba(255,255,255,0.9))",
                  pointerEvents: "none",
                }}
              >
                <KeyboardArrowDownRounded
                  sx={{
                    fontSize: 20,
                    color: "text.secondary",
                    animation: "bounce 2s infinite",
                    "@keyframes bounce": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-4px)" },
                    },
                  }}
                />
              </Box>
            </Fade>
          )}
        </AnimatePresence>
      </Box>

      {/* Footer buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: "auto",
        }}
      >
        <Box
          sx={{
            p: "0.5rem",
            width: "fit-content",
            border: "1px solid #e5e5e5",
            borderRadius: "5rem",
            display: "flex",
            flexDirection: openSide ? "column" : "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.5rem",
            mt: "auto",
          }}
        >
          <Tooltip title="Give Feedback" placement="top">
            <IconButton
              size="small"
              onClick={() =>
                showDialog(
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        p: "1rem",
                      }}
                    >
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            type="email"
                            size="small"
                            placeholder="write that email in which you want update."
                            fullWidth
                            sx={{ fontSize: "0.875rem" }}
                          />
                        )}
                      />

                      <Controller
                        name="rating"
                        control={control}
                        render={({ field }) => (
                          <Box>
                            <InputLabel
                              sx={{ fontSize: "0.875rem", mb: "0.5rem" }}
                            >
                              Rating
                            </InputLabel>
                            <Rating
                              {...field}
                              value={field.value || 0}
                              onChange={(event, newValue) =>
                                field.onChange(newValue)
                              }
                              size="small"
                            />
                          </Box>
                        )}
                      />

                      <Controller
                        name="feedback_type"
                        control={control}
                        render={({ field }) => (
                          <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontSize: "0.875rem" }}>
                              Feedback Type
                            </InputLabel>
                            <Select
                              {...field}
                              label="Feedback Type"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {[
                                { key: "website", label: "Website Experience" },
                                {
                                  key: "case_management",
                                  label: "Case Management Tools",
                                },
                                {
                                  key: "communication",
                                  label: "Client Communication",
                                },
                                {
                                  key: "billing",
                                  label: "Billing & Invoicing",
                                },
                                {
                                  key: "resources",
                                  label: "Legal Resource Access",
                                },
                                { key: "support", label: "Support & Helpdesk" },
                                { key: "other", label: "Other" },
                              ].map((opt, i) => (
                                <MenuItem
                                  key={i}
                                  value={opt.key}
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />

                      <Controller
                        name="comments"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder="Additional comments or suggestions"
                            multiline
                            rows={3}
                            fullWidth
                            label="Description"
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        height: "fit-content",
                        borderTop: "1px solid #ddd",
                        position: "sticky",
                        bottom: 0,
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        p: "0.5rem",
                        zIndex: 1000,
                        gap: "0.5rem",
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                      }}
                    >
                      <Button
                        type="button"
                        onClick={() => closeDialog()}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          minHeight: "2rem",
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        loading={feedSending}
                        type="submit"
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          minHeight: "2rem",
                        }}
                      >
                        Submit
                      </LoadingButton>
                    </Box>
                  </form>,
                  { dialogTitle: "Help Us Improve Your Legal Journey" },
                )
              }
              sx={{
                color: primaryColor,
                bgcolor: hexToRgba(primaryColor, 0.08),
                "&:hover": {
                  bgcolor: hexToRgba(primaryColor, 0.16),
                },
              }}
            >
              <ReviewsRoundedIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Wish List" placement="top">
            <IconButton
              size="small"
              onClick={() =>
                window.open(
                  "https://wishlist.lexmom.com/",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              sx={{
                color: primaryColor,
                bgcolor: hexToRgba(primaryColor, 0.08),
                "&:hover": {
                  bgcolor: hexToRgba(primaryColor, 0.16),
                },
              }}
            >
              <Diversity1Icon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>{" "}
          <Tooltip title="Report Issue" placement="top">
            <IconButton
              size="small"
              onClick={() =>
                showDialog(
                  <form onSubmit={handleSubmit(IssueSubmit)}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        p: "1rem",
                      }}
                    >
                      <Controller
                        name="request_type"
                        control={control}
                        render={({ field }) => (
                          <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontSize: "0.875rem" }}>
                              Request Type
                            </InputLabel>
                            <Select
                              {...field}
                              label="Request Type"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {[
                                { key: "complaint", label: "Complaint" },
                                { key: "feature", label: "Feature Request" },
                              ].map((opt, i) => (
                                <MenuItem
                                  key={i}
                                  value={opt.key}
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />

                      <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Subject"
                            type="text"
                            size="small"
                            placeholder="Brief description of your issue"
                            fullWidth
                            sx={{ fontSize: "0.875rem" }}
                          />
                        )}
                      />

                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder="Detailed description of the issue or feature request"
                            multiline
                            rows={3}
                            fullWidth
                            label="Description"
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        height: "fit-content",
                        borderTop: "1px solid #ddd",
                        position: "sticky",
                        bottom: 0,
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        p: "0.5rem",
                        zIndex: 1000,
                        gap: "0.5rem",
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                      }}
                    >
                      <Button
                        type="button"
                        onClick={() => closeDialog()}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          minHeight: "2rem",
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        loading={issueSending}
                        type="submit"
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          minHeight: "2rem",
                        }}
                      >
                        Submit Issue
                      </LoadingButton>
                    </Box>
                  </form>,
                  { dialogTitle: "Report an Issue or Request Support" },
                )
              }
              sx={{
                color: secondaryColor,
                bgcolor: hexToRgba(secondaryColor, 0.08),
                "&:hover": {
                  bgcolor: hexToRgba(secondaryColor, 0.16),
                },
              }}
            >
              <BugReportRoundedIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supports" placement="top">
            <IconButton
              size="small"
              onClick={() =>
                window.open(
                  "https://support.lexmom.com/en/",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              sx={{
                color: "text.secondary",
                bgcolor: hexToRgba(primaryColor, 0.08),
                "&:hover": {
                  bgcolor: hexToRgba(primaryColor, 0.16),
                },
              }}
            >
              <SupportAgentIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(SideNavBar);
